import React, { useState, useCallback, useEffect } from 'react';
import { Lookup, type LookupOption } from '@/components/ui/lookup';
import { mapsApi } from '@/lib/api/endpoints';

interface PlacePrediction {
  id: string;
  name: string;
  description: string;
  secondary?: string;
  types: string[];
  source_provider: string;
  source_reference: string;
}

interface PlaceDetail {
  place_id: string;
  name: string;
  formatted_address: string;
  display_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  types: string[];
  url?: string;
  business_status?: string;
  vicinity?: string;
}

export interface PlaceAutocompleteProps {
  className?: string;
  disabled?: boolean;
  onPlaceSelected?: (place: PlacePrediction | null) => void;
  onPlaceDetail?: (place: PlaceDetail | null) => void;
  noPlaceDetail?: boolean;
  fields?: string[];
  placeholder?: string;
  predictionTypes?: string;
  predictionCountryRestrictions?: string[];
  emptyMessage?: string;
  renderOption?: (option: LookupOption<PlacePrediction>) => React.ReactNode;
}

const DEFAULT_FIELDS = [
  'place_id',
  'type',
  'url',
  'address_component',
  'name',
  'business_status',
  'formatted_address',
  'geometry',
  'vicinity',
];

// const DEFAULT_FIELDS_NEW = [
//   'id',
//   'moved_place',
//   'moved_place_id',
//   'name',
//   'displayName',
//   'photos',
//   'addressComponents',
//   'formattedAddress',
//   'location',
//   'postalAddress',
//   'shortFormattedAddress',
//   'types',
//   'viewport',
// ];

export function PlaceAutocomplete(props: PlaceAutocompleteProps) {
  const [sessionToken, setSessionToken] = useState<string>('');
  const [predictions, setPredictions] = useState<LookupOption<PlacePrediction>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<LookupOption<PlacePrediction> | null>(null);

  // Initialize session token
  useEffect(() => {
    const generateToken = () => {
      return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    };
    setSessionToken(generateToken());
  }, []);

  // Fetch predictions from API
  const handleSearch = useCallback(
    async (query: string) => {
      if (!query || !sessionToken) {
        setPredictions([]);
        return;
      }

      setIsLoading(true);
      try {
        const params: any = {
          input: query,
          sessionToken,
        };

        if (props.predictionTypes) {
          params.types = props.predictionTypes;
        }

        if (props.predictionCountryRestrictions) {
          params.components = props.predictionCountryRestrictions
            .map((c) => `country:${c}`)
            .join('|');
        }

        const response = await mapsApi.getPlaceAutocomplete(params);

        const options: LookupOption<PlacePrediction>[] = (response.predictions || []).map((p) => ({
          id: p.id,
          label: p.description,
          data: p,
        }));

        setPredictions(options);
      } catch (error) {
        console.error('Failed to fetch place predictions:', error);
        setPredictions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionToken, props.predictionTypes, props.predictionCountryRestrictions]
  );

  // Fetch place details
  const fetchPlaceDetails = useCallback(
    async (placeId: string) => {
      if (!placeId || !sessionToken) return;

      try {
        const params: any = {
          id: placeId,
          sessionToken,
          fields: (props.fields || DEFAULT_FIELDS).join(','),
        };

        const response = await mapsApi.getPlaceDetails(params);

        if (response.status === 'OK' && response.result) {
          props.onPlaceDetail?.(response.result);
          // Generate new session token after successful place details fetch
          setSessionToken(`${Date.now()}-${Math.random().toString(36).substring(2, 15)}`);
        }
      } catch (error) {
        console.error('Failed to fetch place details:', error);
      }
    },
    [sessionToken, props.fields, props.onPlaceDetail]
  );

  // Handle selection
  const handleChange = useCallback(
    (option: LookupOption<PlacePrediction> | null) => {
      setSelectedPlace(option);

      if (!option) {
        props.onPlaceSelected?.(null);
        if (!props.noPlaceDetail) {
          props.onPlaceDetail?.(null);
        }
      } else {
        props.onPlaceSelected?.(option.data);

        if (!props.noPlaceDetail) {
          fetchPlaceDetails(option.data.id);
        }
      }
    },
    [props, fetchPlaceDetails]
  );

  return (
    <Lookup<PlacePrediction>
      value={selectedPlace}
      onChange={handleChange}
      onSearch={handleSearch}
      options={predictions}
      isLoading={isLoading}
      disabled={!sessionToken || props.disabled}
      placeholder={props.placeholder || 'Search for a place...'}
      emptyMessage={props.emptyMessage || 'No places found'}
      className={props.className}
      renderOption={props.renderOption}
    />
  );
}

export default PlaceAutocomplete;
